import { INVALID_CHARACTERS } from "@/shared/config/constants";

export const useValidators = () => {
  // 文字列（バイト数）バリデーション
  const limitStringBytes = ({
    val,
    byteLengthMax,
    byteLengthMin,
    name,
  }: {
    val: string; // 値
    byteLengthMax: number; // 最大バイト数
    byteLengthMin?: number; // 最小バイト数（初期値：0）
    name?: string; // エラーメッセージに表示する項目名
  }): string => {
    // 半角1バイト、全角2バイトでバイト総数を算出
    const countBytes = function (str: string) {
      let length = 0;
      for (let i = 0; i < str.length; i++) {
        const c = str.charCodeAt(i);
        if (
          (c >= 0x0 && c < 0x81) ||
          c === 0xf8f0 ||
          (c >= 0xff61 && c < 0xffa0) ||
          (c >= 0xf8f1 && c < 0xf8f4)
        ) {
          length += 1;
        } else {
          length += 2;
        }
      }
      return length;
    };

    const createMessage = () => {
      let message = "";
      if (name) {
        message += `${name}は`;
      }
      if (byteLengthMin) {
        const zenkakuBytesMin = byteLengthMin / 2;
        message += `半角${byteLengthMin}文字（全角${zenkakuBytesMin}文字）以上、`;
      }

      const zenkakuBytesMax = byteLengthMax / 2;
      message += `半角${byteLengthMax}文字（全角${zenkakuBytesMax}文字）以下で入力してください。`;
      return message;
    };

    if (countBytes(val) < (byteLengthMin ?? 0) || countBytes(val) > byteLengthMax) {
      return createMessage();
    }
    return "";
  };

  // 文字数バリデーション
  const limitStringLength = ({
    val,
    stringLengthMax,
    stringLengthMin,
    name,
  }: {
    val: string; // 値
    stringLengthMax: number; // 最大文字数
    stringLengthMin?: number; // 最小文字数（初期値：0）
    name?: string; // エラーメッセージに表示する項目名
  }) => {
    const createMessage = () => {
      let message = "";
      if (name) {
        message += `${name}は`;
      }
      if (stringLengthMin) {
        message += `${stringLengthMin}文字以上、`;
      }
      message += `${stringLengthMax}文字以下で入力してください。`;
      return message;
    };

    if (stringLengthMin && val.length < stringLengthMin) {
      return createMessage();
    }
    if (val.length > stringLengthMax) {
      return createMessage();
    }
    return "";
  };

  /**
   * 数値範囲のバリデーション
   * 値が上限と下限の範囲内か判定してboolean値を返す
   * 上限以下かつ下限以上ならtrue、そうでなければfalseを返す
   */
  const isInRange = (val: number, max: number, min: number) => {
    if (val < min || val > max) {
      return false;
    }
    return true;
  };

  /**
   * 数値のバリデーション
   * 正常の場合は空文字、異常の場合はエラーメッセージを返す
   */
  const limitNumber = ({
    val,
    max,
    min,
    name,
  }: {
    val: number; // 値
    max: number; // 最大値
    min: number; // 最小値
    name?: string; // エラーメッセージに表示する項目名
  }) => {
    const createMessage = () => {
      let message = "";
      if (name) {
        message += `${name}は`;
      }
      message += `${min}~${max}の数値を入力してください。`;
      return message;
    };
    if (!isInRange(val, max, min)) {
      return createMessage();
    }
    return "";
  };

  /**
   * 数値のバリデーション
   * 正常の場合は空文字、異常の場合はエラーメッセージを返す
   * (limitNumber関数とは異常時のエラーメッセージが異なる)
   */
  const limitNumberRange = ({
    val,
    max,
    min,
    name,
    targetName,
  }: {
    val: number; // 値
    max: number; // 最大値
    min: number; // 最小値
    name?: string; // エラーメッセージに表示する項目名
    targetName?: string; // エラーメッセージに表示する項目名
  }) => {
    const createMessage = () => {
      let message = "";
      if (name) {
        message += `${name}は、`;
      }
      message += `${min.toLocaleString()}~${max.toLocaleString()}の範囲になるように`;
      if (targetName) {
        message += `${targetName}を`;
      }
      message += "入力してください。";
      return message;
    };
    if (!isInRange(val, max, min)) {
      return createMessage();
    }
    return "";
  };

  // メールアドレスのバリデーション（RFC規格）
  const mailAddressValidator = (val: string) => {
    // 文字数制限チェック（250文字以内）
    const lengthLimit = limitStringLength({
      val: val,
      stringLengthMax: 250,
    });
    if (lengthLimit) {
      return lengthLimit;
    }

    // メールアドレス正規表現
    const emailRegExp =
      /^[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9]([a-zA-Z0-9\-_]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9]([a-zA-Z0-9\-_]*[a-zA-Z0-9])?$/;

    // 必須項目ではないので、空文字でもエラーが出ないように修正
    if (!emailRegExp.test(val) && val !== "") {
      return "正しいメールアドレスを入力してください。";
    }
    return "";
  };

  // メールアドレスのバリデーション（インストール版とRFC版のハイブリッド）
  // ログイン・初回認証画面向け：下記条件をチェック
  // 1. @ が存在する
  // 2. ローカル部が空でない
  // 3. ローカル部が半角英数字・-・_・.・+ のみ
  // 4. ドメイン部が空でない
  // 5. ドメインの末尾文字が英数字
  // 6. ドメインに . が含まれ、2ブロック以上ある
  // 7. 各ブロックが空でない（先頭・末尾・連続ドット不可）
  const looseMailAddressValidator = (val: string) => {
    const emailRegExp =
      /^[a-zA-Z0-9_+.\-]+@([a-zA-Z0-9_\-]+\.)+[a-zA-Z0-9]([a-zA-Z0-9_\-]*[a-zA-Z0-9])?$/;

    if (!emailRegExp.test(val) && val !== "") {
      return "正しいメールアドレスを入力してください。";
    }
    return "";
  };

  // パスワードのバリデーション
  const passwordValidator = (val: string) => {
    // パスワード正規表現（英数字8~15文字）
    // ASCII 標準文字のみ
    const passwordRegExp = /^[!-~]{8,15}$/;

    if (!passwordRegExp.test(val)) {
      return "パスワードは8~15文字で半角英数字を使用してください。";
    }

    return "";
  };

  /**
   * 数字コードバリデーション
   * @param numeralCode 数字コードを表す文字列
   * @param maxDigit コードの最大桁数
   * @param minDigit コードの最小桁数(既定値は1, 省略可)
   */
  const numeralCodeValidator = (numeralCode: string, maxDigit: number, minDigit = 1) => {
    const { length } = numeralCode;
    return Number.isInteger(maxDigit) && maxDigit > 0 && minDigit > 0
      ? length <= maxDigit && new RegExp(`^[0-9]{${minDigit},${maxDigit}}$`).test(numeralCode)
        ? ""
        : `0から9の半角数字を${minDigit}〜${maxDigit}桁で入力してください。`
      : "コードの桁数には正の整数を指定してください。";
  };

  // 半角数字バリデーション
  const OneByteNumValidator = (val: string) => {
    if (val.match(/^[0-9]+$/)) {
      return "";
    } else {
      return "半角数字で入力してください。";
    }
  };

  /**
   * 開始日・終了日バリデーション
   * @param {string | undefined} args.startDate - 開始日
   * @param {string | undefined} args.endDate - 終了日
   */
  const fromToDateValidator = (args: {
    startDate: string | undefined;
    endDate: string | undefined;
  }) => {
    // 開始日が未指定の場合は制限なく終了日を設定可能のため、エラーメッセージを空として返す
    if (args.startDate === "" || args.startDate === undefined) {
      return "";
    }

    // 終了日が未指定の場合は制限なく開始日を設定可能のため、エラーメッセージを空として返す
    if (args.endDate === "" || args.endDate === undefined) {
      return "";
    }

    // 開始日よりも設定しようとした終了日が過去の場合はバリデーションエラーを返す
    if (args.endDate < args.startDate) {
      return "終了日は開始日以降の日付を設定してください。";
    }

    return "";
  };

  /**
   * hex32のバリデーション
   * @param value - 検証する値
   * @returns boolean - true: 有効, false: 無効
   */
  const isHex32 = (value: unknown): boolean => {
  if (typeof value !== "string") {
    return false;
  }
  return /^[0-9a-fA-F]{32}$/.test(value);
}

  /**
   * 日付バリデーション関数（存在しない日付・空欄）
   * @param {string} date - 文書日付全般
   */
  const documentDateValidater = (date: string | undefined) => {
    if (date) {
      return "";
    } else {
      return "存在する日付を入力してください";
    }
  };

  /*--------------------
    ユーザー設定関連
  --------------------*/
  /**
   * ユーザー名バリデーション（32文字以内）
   * @param {string} userName - ユーザー名
   */
  const userNameValidator = (userName: string) => {
    // 空文字は許容
    if (!userName) return "";
    // 許容できない文字をチェック
    if (INVALID_CHARACTERS.test(userName)) {
      return "ユーザー名に使用できない文字・記号が含まれています。";
    }
    // 文字数チェック
    return limitStringLength({
      val: userName,
      stringLengthMax: 50,
    });
  };

  /**
   * メールアドレス変更バリデーション
   * @param {string} email - パスワード
   * @param {string} confirmMailAddress - パスワード（確認用）
   */
  const confirmMailAddressSettingModalValidator = (
    email: string,
    confirmMailAddress: string,
  ) => {
    let errorMessage = mailAddressValidator(confirmMailAddress);

    if (!errorMessage) {
      errorMessage = mailAddressMatchValidator(email, confirmMailAddress);
    }

    return errorMessage;
  };

  /**
   * メールアドレス一致バリデーション
   * @param {string} email - パスワード
   * @param {string} confirmMailAddress - パスワード（確認用）
   */
  const mailAddressMatchValidator = (email: string, confirmMailAddress: string) => {
    if (email === confirmMailAddress) {
      return "";
    } else {
      return "確認用のメールアドレスが一致していません。同じメールアドレスを入力してください。";
    }
  };

  /**
   * パスワード変更バリデーション
   * @param {string} password - パスワード
   * @param {string} confirmPassword - パスワード（確認用）
   */
  const confirmPasswordSettingNodalValidator = (password: string, confirmPassword: string) => {
    let errorMessage = passwordValidator(confirmPassword);

    if (!errorMessage) {
      errorMessage = passwordMatchValidator(password, confirmPassword);
    }

    return errorMessage;
  };

  /**
   * パスワード一致バリデーション
   * @param {string} password - パスワード
   * @param {string} confirmPassword - パスワード（確認用）
   */
  const passwordMatchValidator = (password: string, confirmPassword: string) => {
    if (password === confirmPassword) {
      return "";
    } else {
      return "確認用のパスワードが一致していません。同じパスワードを入力してください。";
    }
  };

  /**
   * 事業者名バリデーション（25文字以内）
   * @param {string} companyName - 事業者名
   */
  const companyNameValidator = (companyName: string) => {
    return limitStringLength({
      val: companyName,
      stringLengthMax: 25,
    });
  };

  /**
   * 氏名バリデーション（16文字以内）
   * @param {string} name - 氏名
   */
  const nameValidator = (name: string) => {
    if (name) {
      return limitStringLength({
        val: name,
        stringLengthMax: 16,
      });
    } else {
      return "入力してください。";
    }
  };

  /**
   * PDFタイトルバリデーション（20文字以内）
   * @param {string} title - タイトル
   */
  const pdfFreeTitleValidator = (title: string) => {
    if (title) {
      return limitStringLength({
        val: title,
        stringLengthMax: 20,
      });
    } else {
      return "入力してください。";
    }
  };

  /**
   * PDF品目の項目名バリデーション（20文字以内）
   * @param {string} itemTitle - 品目の項目名
   */
  const detailItemFreeTitleValidator = (itemTitle: string) => {
    if (itemTitle) {
      return limitStringLength({
        val: itemTitle,
        stringLengthMax: 20,
      });
    } else {
      return "入力してください。";
    }
  };

  /*--------------------
    住所関連
  --------------------*/
  // 郵便番号桁数
  const POSTAL_ZONE_MAX_LENGTH = 7;
  /**
   * 郵便番号バリデーション
   * @param {string} postalZone - 郵便番号
   */
  const postalZoneValidator = (postalZone: string) => {
    if (postalZone) {
      //必須項目ではないので、空文字でも通すように変更
      return postalZone.replace(/-/g, "").length <= POSTAL_ZONE_MAX_LENGTH &&
        /^[0-9]{3}-?[0-9]{4}$/.test(postalZone)
        ? ""
        : "「数字3桁-数字4桁」で入力してください(ハイフン'-'は省略可能)。";
    } else {
      return "";
    }
  };

  /**
   * 都道府県バリデーション（4文字以内）
   * @param {string} prefectureName - 都道府県
   */
  const prefectureNameValidator = (prefectureName: string) => {
    return limitStringLength({
      val: prefectureName,
      stringLengthMax: 4,
    });
  };

  /**
   * 市区町村バリデーション（15文字以内）
   * @param {string} cityName - 市区町村
   */
  const cityNameValidator = (cityName: string) => {
    return limitStringLength({
      val: cityName,
      stringLengthMax: 15,
    });
  };

  /**
   * 市区町村以降バリデーション（25文字以内）
   * @param {string} streetName - 市区町村以降
   */
  const streetNameValidator = (streetName: string) => {
    return limitStringLength({
      val: streetName,
      stringLengthMax: 25,
    });
  };

  /**
   * マンション名などバリデーション（50文字以内）
   * @param {string} buildingName - マンション名など
   */
  const buildingNameValidator = (buildingName: string) => {
    return limitStringLength({
      val: buildingName,
      stringLengthMax: 50,
    });
  };

  /*--------------------
    口座関連
  --------------------*/
  /**
   * 金融機関名バリデーション（20文字以内）
   * @param {string} institutionName - 金融機関名
   */
  const institutionNameValidator = (institutionName: string) => {
    return limitStringLength({
      val: institutionName,
      stringLengthMax: 30,
    });
  };

  /**
   * 金融機関コードバリデーション（半角数字4文字）
   * @param {string} institutionCode - 金融機関コード
   */
  const institutionCodeValidator = (institutionCode: string) => {
    if (institutionCode === "") return "";
    const oneByteNumVal = OneByteNumValidator(institutionCode);
    if (oneByteNumVal) {
      return oneByteNumVal;
    } else {
      return limitStringLength({
        val: institutionCode,
        stringLengthMax: 4,
      });
    }
  };

  /**
   * 支店名バリデーション（20文字以内）
   * @param {string} branchName - 支店名
   */
  const branchNameValidator = (branchName: string) => {
    if (branchName === "") return "";
    return limitStringLength({
      val: branchName,
      stringLengthMax: 30,
    });
  };

  /**
   * 支店名コードバリデーション（半角数字3文字）
   * @param {string} branchCode - 支店名コード
   */
  const branchCodeValidator = (branchCode: string) => {
    if (branchCode === "") return "";
    const oneByteNumVal = OneByteNumValidator(branchCode);
    if (oneByteNumVal) {
      return oneByteNumVal;
    } else {
      return limitStringLength({
        val: branchCode,
        stringLengthMax: 3,
      });
    }
  };

  /**
   * 口座番号バリデーション
   * @param {string} financeNumber - 口座番号
   */
  const financeNumberValidator = (financeNumber: string) => {
    if (financeNumber) {
      return /^[0-9]{7}$/.test(financeNumber) ? "" : "「数字7桁」で入力してください。";
    }
    return "";
  };

  /**
   * ゆうちょ銀行記号バリデーション
   * @param {string} yuchoSymbol - 記号
   */
  const yuchoSymbolValidator = (yuchoSymbol: string) => {
    if (yuchoSymbol) {
      return /^[0-9]{5}$/.test(yuchoSymbol) ? "" : "「数字5桁」で入力してください。";
    }
    return "";
  };

  /**
   * ゆうちょ銀行番号バリデーション
   * @param {string} yuchoNumber - 番号
   */
  const yuchoNumberValidator = (yuchoNumber: string) => {
    if (yuchoNumber) {
      return /^[0-9]{2,8}$/.test(yuchoNumber) ? "" : "「数字2〜8桁」で入力してください。";
    }
    return "";
  };

  return {
    // 共通
    limitStringBytes,
    limitStringLength,
    limitNumber,
    limitNumberRange,
    mailAddressValidator,
    looseMailAddressValidator,
    passwordValidator,
    numeralCodeValidator,
    fromToDateValidator,
    isHex32,

    // 文書関連
    documentDateValidater,

    // 住所関連
    postalZoneValidator,
    prefectureNameValidator,
    cityNameValidator,
    streetNameValidator,
    buildingNameValidator,

    // 口座関連
    institutionNameValidator,
    institutionCodeValidator,
    branchNameValidator,
    branchCodeValidator,
    financeNumberValidator,
    yuchoSymbolValidator,
    yuchoNumberValidator,

    // ユーザー設定関連
    userNameValidator,
    confirmMailAddressSettingModalValidator,
    mailAddressMatchValidator,
    confirmPasswordSettingNodalValidator,
    passwordMatchValidator,
    companyNameValidator,
    nameValidator,
    pdfFreeTitleValidator,
    detailItemFreeTitleValidator,

    // 数値範囲のバリデーション
    isInRange,
  };
};
